import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface FeePayment {
  id: string;
  period: string;
  amount: number;
  method: "UPI" | "Bank Transfer" | "Cash" | "Credit Card" | "Cheque";
  date: string;
  screenshot?: string;
  status: "Paid" | "Pending" | "Overdue";
}

export interface StudentDocument {
  id: string;
  type: string;
  fileName: string;
  uploadedAt: string;
  percentage?: number;
}

export interface StudentLead {
  id: string;
  name: string;
  email: string;
  phone: string;
  password: string;
  stage: string;
  status: string;
  subStatus: string;
  course: string;
  portalEnabled: boolean;
  avatar: string;
  fees: FeePayment[];
  documents: StudentDocument[];
  totalFees: number;
  paidFees: number;
  enrollmentDate: string;
  yearOfStudy: number;
}

// ─── Config Types ───────────────────────────────────────────────────────────

export interface FeeStructureConfig {
  collectionCycle: "Yearly" | "Semester" | "Custom";
  programDuration: number;
  baseFeePerPeriod: number;
  allowPartPayments: boolean;
  gracePeriodDays: number;
}

export interface AddonFee {
  id: string;
  name: string;
  amount: number;
  mandatory: boolean;
  recurring: boolean;
  active: boolean;
}

export interface PaymentSettings {
  methods: Record<string, boolean>;
  lateFee: { enabled: boolean; type: "percentage" | "flat"; value: number };
  autoReceipt: boolean;
}

export interface PortalSettings {
  masterEnabled: boolean;
  autoActivateAtStage: string;
  passwordMinLength: number;
  requireSpecialChars: boolean;
  sessionTimeoutMinutes: number;
  autoGenerateCredentials: boolean;
  defaultPasswordFormat: "Random" | "Name-based" | "Custom prefix";
  passwordLength: number;
}

export interface DocumentRequirement {
  id: string;
  name: string;
  mandatory: boolean;
  formats: string[];
  active: boolean;
}

export interface PortalMessages {
  welcomeMessage: string;
  loginNotice: string;
  announcement: { text: string; active: boolean };
}

export interface FeeAnalytics {
  totalCollection: number;
  pendingAmount: number;
  overdueCount: number;
  partPaymentCount: number;
}

export interface PortalAnalytics {
  activePortals: number;
  totalLogins30d: number;
  documentsSubmitted: number;
  pendingDocuments: number;
}

export const studentPortalTabs = [
  { label: "Fees Management", value: "Fees Management" },
  { label: "Portal Management", value: "Portal Management" },
];

export type ConfigTabValue = "Fees Management" | "Portal Management";

// ─── Mock Data ──────────────────────────────────────────────────────────────

const mockAddonFees: AddonFee[] = [
  {
    id: "AF1",
    name: "Hostel Fee",
    amount: 85000,
    mandatory: false,
    recurring: true,
    active: true,
  },
  {
    id: "AF2",
    name: "Registration Fee",
    amount: 10000,
    mandatory: true,
    recurring: false,
    active: true,
  },
  {
    id: "AF3",
    name: "Admission Fee",
    amount: 25000,
    mandatory: true,
    recurring: false,
    active: true,
  },
  {
    id: "AF4",
    name: "Exam Fee",
    amount: 3500,
    mandatory: true,
    recurring: true,
    active: true,
  },
  {
    id: "AF5",
    name: "Lab Fee",
    amount: 8000,
    mandatory: false,
    recurring: true,
    active: true,
  },
  {
    id: "AF6",
    name: "Library Fee",
    amount: 2500,
    mandatory: false,
    recurring: true,
    active: false,
  },
  {
    id: "AF7",
    name: "Sports Fee",
    amount: 5000,
    mandatory: false,
    recurring: true,
    active: false,
  },
];

const mockDocumentRequirements: DocumentRequirement[] = [
  {
    id: "DR1",
    name: "10th Certificate",
    mandatory: true,
    formats: ["PDF", "JPG", "PNG"],
    active: true,
  },
  {
    id: "DR2",
    name: "12th Certificate",
    mandatory: true,
    formats: ["PDF", "JPG", "PNG"],
    active: true,
  },
  {
    id: "DR3",
    name: "Degree Certificate",
    mandatory: false,
    formats: ["PDF"],
    active: true,
  },
  {
    id: "DR4",
    name: "Aadhaar Card",
    mandatory: true,
    formats: ["PDF", "JPG"],
    active: true,
  },
  {
    id: "DR5",
    name: "Passport",
    mandatory: false,
    formats: ["PDF", "JPG"],
    active: true,
  },
  {
    id: "DR6",
    name: "Migration Certificate",
    mandatory: false,
    formats: ["PDF"],
    active: true,
  },
  {
    id: "DR7",
    name: "Medical Certificate",
    mandatory: false,
    formats: ["PDF", "JPG"],
    active: false,
  },
];

// ─── State ──────────────────────────────────────────────────────────────────

export interface StudentPortalState {
  currentTab: ConfigTabValue;

  feeStructure: FeeStructureConfig;
  addonFees: AddonFee[];
  paymentSettings: PaymentSettings;
  feeAnalytics: FeeAnalytics;

  portalSettings: PortalSettings;
  documentRequirements: DocumentRequirement[];
  portalMessages: PortalMessages;
  portalAnalytics: PortalAnalytics;
}

const initialState: StudentPortalState = {
  currentTab: "Fees Management",

  feeStructure: {
    collectionCycle: "Semester",
    programDuration: 3,
    baseFeePerPeriod: 120000,
    allowPartPayments: true,
    gracePeriodDays: 15,
  },
  addonFees: mockAddonFees,
  paymentSettings: {
    methods: {
      Gateway: true,
      UPI: true,
      "Bank Transfer": true,
      Cash: true,
      Cheque: false,
      Manual: true,
    },
    lateFee: { enabled: true, type: "percentage", value: 5 },
    autoReceipt: true,
  },
  feeAnalytics: {
    totalCollection: 28450000,
    pendingAmount: 8920000,
    overdueCount: 12,
    partPaymentCount: 34,
  },

  portalSettings: {
    masterEnabled: true,
    autoActivateAtStage: "Application",
    passwordMinLength: 8,
    requireSpecialChars: true,
    sessionTimeoutMinutes: 30,
    autoGenerateCredentials: true,
    defaultPasswordFormat: "Random",
    passwordLength: 10,
  },
  documentRequirements: mockDocumentRequirements,
  portalMessages: {
    welcomeMessage:
      "Welcome to your student portal! Here you can track your application progress, upload required documents, and manage your fee payments.",
    loginNotice:
      "Use the credentials provided by your counsellor to access your portal.",
    announcement: {
      text: "Last date for 2nd semester fee payment is 15th January 2026. Late fee of 5% will be applicable after the due date.",
      active: true,
    },
  },
  portalAnalytics: {
    activePortals: 142,
    totalLogins30d: 1847,
    documentsSubmitted: 389,
    pendingDocuments: 56,
  },

};

// ─── Slice ──────────────────────────────────────────────────────────────────

const studentPortalSlice = createSlice({
  name: "studentPortal",
  initialState,
  reducers: {
    setCurrentTab(state, action: PayloadAction<ConfigTabValue>) {
      state.currentTab = action.payload;
    },

    // Fee structure
    updateFeeStructure(
      state,
      action: PayloadAction<Partial<FeeStructureConfig>>
    ) {
      Object.assign(state.feeStructure, action.payload);
    },

    // Add-on fees
    addAddonFee(state, action: PayloadAction<Omit<AddonFee, "id">>) {
      state.addonFees.push({ ...action.payload, id: `AF${Date.now()}` });
    },
    updateAddonFee(
      state,
      action: PayloadAction<{ id: string; changes: Partial<AddonFee> }>
    ) {
      const fee = state.addonFees.find((f) => f.id === action.payload.id);
      if (fee) Object.assign(fee, action.payload.changes);
    },
    removeAddonFee(state, action: PayloadAction<string>) {
      state.addonFees = state.addonFees.filter((f) => f.id !== action.payload);
    },
    toggleAddonFee(state, action: PayloadAction<string>) {
      const fee = state.addonFees.find((f) => f.id === action.payload);
      if (fee) fee.active = !fee.active;
    },

    // Payment settings
    updatePaymentSettings(
      state,
      action: PayloadAction<Partial<PaymentSettings>>
    ) {
      Object.assign(state.paymentSettings, action.payload);
    },
    togglePaymentMethod(state, action: PayloadAction<string>) {
      state.paymentSettings.methods[action.payload] =
        !state.paymentSettings.methods[action.payload];
    },
    updateLateFee(
      state,
      action: PayloadAction<Partial<PaymentSettings["lateFee"]>>
    ) {
      Object.assign(state.paymentSettings.lateFee, action.payload);
    },

    // Portal settings
    updatePortalSettings(
      state,
      action: PayloadAction<Partial<PortalSettings>>
    ) {
      Object.assign(state.portalSettings, action.payload);
    },

    // Document requirements
    addDocumentRequirement(
      state,
      action: PayloadAction<Omit<DocumentRequirement, "id">>
    ) {
      state.documentRequirements.push({
        ...action.payload,
        id: `DR${Date.now()}`,
      });
    },
    updateDocumentRequirement(
      state,
      action: PayloadAction<{
        id: string;
        changes: Partial<DocumentRequirement>;
      }>
    ) {
      const doc = state.documentRequirements.find(
        (d) => d.id === action.payload.id
      );
      if (doc) Object.assign(doc, action.payload.changes);
    },
    removeDocumentRequirement(state, action: PayloadAction<string>) {
      state.documentRequirements = state.documentRequirements.filter(
        (d) => d.id !== action.payload
      );
    },
    toggleDocumentRequirement(state, action: PayloadAction<string>) {
      const doc = state.documentRequirements.find(
        (d) => d.id === action.payload
      );
      if (doc) doc.active = !doc.active;
    },

    // Portal messages
    updatePortalMessages(
      state,
      action: PayloadAction<Partial<PortalMessages>>
    ) {
      Object.assign(state.portalMessages, action.payload);
    },
    updateAnnouncement(
      state,
      action: PayloadAction<Partial<PortalMessages["announcement"]>>
    ) {
      Object.assign(state.portalMessages.announcement, action.payload);
    },

  },
});

export const {
  setCurrentTab,
  updateFeeStructure,
  addAddonFee,
  updateAddonFee,
  removeAddonFee,
  toggleAddonFee,
  updatePaymentSettings,
  togglePaymentMethod,
  updateLateFee,
  updatePortalSettings,
  addDocumentRequirement,
  updateDocumentRequirement,
  removeDocumentRequirement,
  toggleDocumentRequirement,
  updatePortalMessages,
  updateAnnouncement,
} = studentPortalSlice.actions;

export default studentPortalSlice.reducer;
